/** Tax invoice ka poora hisaab — ek jagah, bina React ke, taaki test ho sake.
 *
 *  Bill ka dhaancha (jo kagaz par chhapta hai):
 *    GROSS        = har line ka (qty x rate - discount)
 *    TOTAL ADD    = Packing + Transport + ADD + MISC
 *    TOTAL LESS   = jo kaatna hai
 *    Add Tax      = CGST+SGST (apne state me) ya IGST (bahar)
 *    Net Payable  = GROSS + ADD - LESS + Tax + TCS, round off ke saath
 *
 *  Charges (packing/transport) par GST: GST law me ye supply ki value ka
 *  hissa hai, isliye default on hai aur line ke taxable ke anupaat me
 *  baant diya jaata hai — har rate slab ko uska hissa milta hai. Jis bill
 *  me ye nahi chahiye, wahan taxCharges = false.
 */

export const FIRM = {
    legalName: "BLUE PEARL",
    fullName: "BLUE PEARL INDUSTRIES",
    address: "Shanti nagar bhilai 3, Bhilai",
    district: "Dist - Durg, Chhattisgarh, 490021",
    mobile: "8871081395",
    gstin: "22BMQPA3694H2ZY",
    email: "connect@bluepearlindustries.com",
    state: "Chhattisgarh",
    stateCode: "22",
    jurisdiction: "DURG",
    bank: {
        name: "central bank of india",
        holder: "BLUE PEARL INDUSTRIES",
        account: "5551013754",
        ifsc: "CBIN0283161",
    },
    terms: [
        "Goods once sold will not be taken back or exchanged.",
        "Bills not paid due date will attract 24% interest.",
    ],
};

export const STATES = [
    ["Andhra Pradesh", "37"], ["Arunachal Pradesh", "12"], ["Assam", "18"], ["Bihar", "10"],
    ["Chandigarh", "04"], ["Chhattisgarh", "22"], ["Dadra & Nagar Haveli and Daman & Diu", "26"],
    ["Delhi", "07"], ["Goa", "30"], ["Gujarat", "24"], ["Haryana", "06"], ["Himachal Pradesh", "02"],
    ["Jammu & Kashmir", "01"], ["Jharkhand", "20"], ["Karnataka", "29"], ["Kerala", "32"],
    ["Ladakh", "38"], ["Lakshadweep", "31"], ["Madhya Pradesh", "23"], ["Maharashtra", "27"],
    ["Manipur", "14"], ["Meghalaya", "17"], ["Mizoram", "15"], ["Nagaland", "13"], ["Odisha", "21"],
    ["Puducherry", "34"], ["Punjab", "03"], ["Rajasthan", "08"], ["Sikkim", "11"],
    ["Tamil Nadu", "33"], ["Telangana", "36"], ["Tripura", "16"], ["Uttar Pradesh", "09"],
    ["Uttarakhand", "05"], ["West Bengal", "19"], ["Andaman & Nicobar Islands", "35"],
];

export const num = (v) => {
    const n = parseFloat(v);
    return Number.isFinite(n) ? n : 0;
};
const r2 = (v) => Math.round((num(v) + Number.EPSILON) * 100) / 100;

/** GSTIN ke pehle do ank = state code. Party ka state isi se pakad lo. */
export function stateFromGstin(gstin) {
    const code = String(gstin || "").trim().slice(0, 2);
    const hit = STATES.find(([, c]) => c === code);
    return hit ? hit[0] : "";
}

export function stateCodeOf(state) {
    const hit = STATES.find(([s]) => s.toLowerCase() === String(state || "").trim().toLowerCase());
    return hit ? hit[1] : "";
}

/** Apna hi state = CGST + SGST, warna IGST. */
export function isIntraState(partyState) {
    const s = String(partyState || "").trim().toLowerCase();
    if (!s) return true; // state na ho to apna hi maan lo (local counter bill)
    return s === FIRM.state.toLowerCase();
}

/** Aaj ki tareekh, apne ghadi ke hisaab se. toISOString() UTC deta hai —
 *  raat 12 se subah 5:30 tak wo pichhla din likh deta tha. */
export function localDate(d = new Date()) {
    const p = (x) => String(x).padStart(2, "0");
    return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
}

/** Invoice number ko Firestore doc id me badlo (slash allowed nahi). */
export function numberKey(no) {
    return String(no || "").trim().toUpperCase().replace(/[/\\#?\s]+/g, "_");
}

export const UNITS = ["PCS", "NOS", "SET", "PRS", "MTR", "KGS", "BOX", "DOZ"];

export function blankLine() {
    return { desc: "", hsn: "", qty: "", unit: "PCS", rate: "", discPct: "", gstPct: 5 };
}

export function blankBill() {
    return {
        no: "",
        date: localDate(),
        party: { name: "", address: "", city: "", state: "", gstin: "", mobile: "" },
        refNo: "", otherRef: "", buyerOrderNo: "", buyerOrderDate: "",
        dispatchDocNo: "", deliveryNoteDate: "", dispatchedThrough: "", destination: "",
        lrNo: "", vehicleNo: "", termsOfDelivery: "",
        lines: [blankLine()],
        counterBill: "", packing: "", transport: "", addAmt: "", misc: "", totalLess: "", tcs: "",
        taxCharges: true,
        notes: "",
    };
}

/**
 * Poora hisaab. Jo bhi kagaz par chhapna hai, sab yahan se aata hai.
 * @param {object} bill blankBill() jaisa object
 * @returns {{lines:object[], rateRows:object[], gross:number, totalAdd:number,
 *   totalLess:number, taxable:number, cgst:number, sgst:number, igst:number,
 *   addTax:number, tcs:number, roundOff:number, net:number, intra:boolean}}
 */
export function computeBill(bill) {
    const b = bill || {};
    const intra = isIntraState(b.party && b.party.state);

    const lines = (b.lines || [])
        .map((l) => {
            const qty = num(l.qty);
            const rate = num(l.rate);
            const amount = r2(qty * rate);
            const disc = r2((amount * num(l.discPct)) / 100);
            return { ...l, qty, rate, amount, disc, base: r2(amount - disc), gstPct: num(l.gstPct) };
        })
        .filter((l) => l.desc || l.qty || l.rate);

    const gross = r2(lines.reduce((a, l) => a + l.base, 0));
    const totalAdd = r2(num(b.packing) + num(b.transport) + num(b.addAmt) + num(b.misc));
    const totalLess = r2(num(b.totalLess));
    const taxCharges = b.taxCharges !== false;

    // Charges ko line ke taxable ke anupaat me baanto (Tally jaisa apportion).
    const netCharge = r2(totalAdd - totalLess);
    const spread = taxCharges && gross > 0 ? netCharge : 0;
    let given = 0;
    const withShare = lines.map((l, i) => {
        let share;
        if (i === lines.length - 1) share = r2(spread - given);
        else { share = r2((spread * l.base) / gross); given = r2(given + share); }
        return { ...l, taxable: r2(l.base + share) };
    });

    const byRate = new Map();
    withShare.forEach((l) => {
        const k = l.gstPct;
        if (!byRate.has(k)) byRate.set(k, { rate: k, taxable: 0, tax: 0 });
        const g = byRate.get(k);
        g.taxable = r2(g.taxable + l.taxable);
    });
    byRate.forEach((g) => { g.tax = r2((g.taxable * g.rate) / 100); });

    const rateRows = [...byRate.values()]
        .sort((a, b2) => a.rate - b2.rate)
        .map((g) => ({
            ...g,
            cgst: intra ? r2(g.tax / 2) : 0,
            sgst: intra ? r2(g.tax / 2) : 0,
            igst: intra ? 0 : g.tax,
        }));

    const cgst = r2(rateRows.reduce((a, g) => a + g.cgst, 0));
    const sgst = r2(rateRows.reduce((a, g) => a + g.sgst, 0));
    const igst = r2(rateRows.reduce((a, g) => a + g.igst, 0));
    const addTax = r2(cgst + sgst + igst);
    const tcs = r2(num(b.tcs));
    const taxable = r2(withShare.reduce((a, l) => a + l.taxable, 0));

    // Charges par GST nahi lagana ho to wo tax ke baad jodte hain.
    const beforeRound = taxCharges
        ? r2(taxable + addTax + tcs)
        : r2(gross + netCharge + addTax + tcs);
    const net = Math.round(beforeRound);
    const roundOff = r2(net - beforeRound);

    return {
        lines: withShare, rateRows,
        gross, totalAdd, totalLess, taxable,
        cgst, sgst, igst, addTax, tcs, roundOff, net, intra,
    };
}

/** 269010 -> "Rupees Two Lakh Sixty Nine Thousand Ten Only" */
export function amountInWords(value) {
    const whole = Math.floor(Math.abs(Math.round(num(value) * 100)) / 100);
    const paise = Math.abs(Math.round(num(value) * 100)) % 100;
    const ones = ["", "One", "Two", "Three", "Four", "Five", "Six", "Seven", "Eight", "Nine", "Ten",
        "Eleven", "Twelve", "Thirteen", "Fourteen", "Fifteen", "Sixteen", "Seventeen", "Eighteen", "Nineteen"];
    const tens = ["", "", "Twenty", "Thirty", "Forty", "Fifty", "Sixty", "Seventy", "Eighty", "Ninety"];
    const two = (x) => (x < 20 ? ones[x] : `${tens[Math.floor(x / 10)]}${x % 10 ? ` ${ones[x % 10]}` : ""}`);
    const three = (x) => {
        const h = Math.floor(x / 100), rest = x % 100;
        return `${h ? `${ones[h]} Hundred${rest ? " " : ""}` : ""}${rest ? two(rest) : ""}`;
    };
    const say = (v) => {
        if (!v) return "Zero";
        let out = "", x = v;
        const cr = Math.floor(x / 1e7); x %= 1e7;
        const lk = Math.floor(x / 1e5); x %= 1e5;
        const th = Math.floor(x / 1e3); x %= 1e3;
        if (cr) out += `${say(cr)} Crore `;
        if (lk) out += `${two(lk)} Lakh `;
        if (th) out += `${two(th)} Thousand `;
        if (x) out += three(x);
        return out.trim();
    };
    const sign = num(value) < 0 ? "Minus " : "";
    if (!paise) return `${sign}Rupees ${say(whole)} Only`;
    return `${sign}Rupees ${say(whole)} and ${two(paise)} Paise Only`;
}

/** Saved bills ka jod — GST return ke liye. Cancelled bill nahi ginte. */
export function billsSummary(bills) {
    const live = (bills || []).filter((b) => !b.cancelled);
    const sum = (k) => r2(live.reduce((a, b) => a + num(b.totals && b.totals[k]), 0));
    return {
        count: live.length,
        cancelled: (bills || []).length - live.length,
        taxable: sum("taxable"),
        cgst: sum("cgst"),
        sgst: sum("sgst"),
        igst: sum("igst"),
        tcs: sum("tcs"),
        net: sum("net"),
    };
}

/** GST wale ko dene layak CSV — ek bill ek line. */
export function billsCsv(bills) {
    const q = (v) => {
        const s = String(v == null ? "" : v);
        return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
    };
    const head = ["Invoice No", "Date", "Party", "GSTIN", "Place of Supply", "State Code",
        "Taxable", "CGST", "SGST", "IGST", "TCS", "Round Off", "Net", "Status"];
    const rows = (bills || []).map((b) => {
        const t = b.totals || {};
        const p = b.party || {};
        return [b.no, b.date, p.name, p.gstin, p.state || FIRM.state, stateCodeOf(p.state || FIRM.state),
            t.taxable, t.cgst, t.sgst, t.igst, t.tcs, t.roundOff, t.net,
            b.cancelled ? "CANCELLED" : "OK"];
    });
    return [head, ...rows].map((r) => r.map(q).join(",")).join("\r\n");
}

/** Bill save karne layak hai? Jo kami hai uski list wapas. */
export function billProblems(bill) {
    const out = [];
    const b = bill || {};
    if (!String(b.no || "").trim()) out.push("Invoice number khaali hai");
    if (!String(b.date || "").trim()) out.push("Date khaali hai");
    if (!String((b.party && b.party.name) || "").trim()) out.push("Party (Bill To) nahi chuni");
    const lines = (b.lines || []).filter((l) => String(l.desc || "").trim());
    if (!lines.length) out.push("Ek bhi item nahi");
    lines.forEach((l, i) => {
        if (num(l.qty) <= 0) out.push(`Line ${i + 1}: qty 0 hai`);
        if (num(l.rate) <= 0) out.push(`Line ${i + 1}: rate 0 hai`);
    });
    const gstin = String((b.party && b.party.gstin) || "").trim();
    if (gstin && !/^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z][0-9A-Z]Z[0-9A-Z]$/.test(gstin.toUpperCase())) {
        out.push("Party ka GSTIN sahi format me nahi");
    } else if (gstin) {
        // GSTIN ke pehle do ank state batate hain. Alag state chuna to tax
        // galat lagega (IGST ki jagah CGST+SGST ya ulta).
        const fromGstin = stateFromGstin(gstin);
        const chosen = String((b.party && b.party.state) || "").trim();
        if (fromGstin && chosen && fromGstin.toLowerCase() !== chosen.toLowerCase()) {
            out.push(`GSTIN ${gstin.slice(0, 2)} = ${fromGstin}, par state ${chosen} chuna hai`);
        }
    }
    return out;
}
